import { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Autocomplete, Box, Typography, CircularProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { getAllAgents } from '../../api/userApi';
import { createExtraSlip } from '../../api/extraSlipApi';

// Match the commission-slip period format: "22 [prevMonth] - 21 [month] YYYY".
// Picking May 2026 -> "22 Apr - 21 May 2026", same cycle accountants already
// expect on every commission slip.
function formatPeriodRange(monthDate) {
    const start = monthDate.subtract(1, 'month').date(22);
    const end = monthDate.date(21);
    return `${start.format('DD MMM')} - ${end.format('DD MMM YYYY')}`;
}

export default function CreateExtraSlipDialog({ open, onClose, onCreated }) {
    const [agents, setAgents] = useState([]);
    const [agent, setAgent] = useState(null);
    const [reason, setReason] = useState('');
    const [description, setDescription] = useState('');
    const [amount, setAmount] = useState('');
    const [period, setPeriod] = useState(dayjs()); // dayjs object — formatted on submit
    const [submitting, setSubmitting] = useState(false);
    const [loadingAgents, setLoadingAgents] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) return;
        // Reset on open
        setAgent(null);
        setReason('');
        setDescription('');
        setAmount('');
        setPeriod(dayjs());
        setError('');
        setLoadingAgents(true);
        getAllAgents()
            .then(res => setAgents(res.data || []))
            .catch(e => {
                console.error(e);
                setAgents([]);
            })
            .finally(() => setLoadingAgents(false));
    }, [open]);

    const submit = async () => {
        setError('');
        if (!agent) { setError('Please select an agent.'); return; }
        if (!reason.trim()) { setError('Please enter a reason.'); return; }
        const amt = parseFloat(amount);
        if (!amt || amt <= 0) { setError('Please enter a valid amount.'); return; }
        setSubmitting(true);
        try {
            const body = {
                agentId: agent.userId ?? agent.UserId,
                reason: reason.trim(),
                description: description?.trim() || null,
                amount: amt,
                period: period && dayjs.isDayjs(period) ? formatPeriodRange(period) : null,
            };
            await createExtraSlip(body);
            onClose?.();
            onCreated?.();
        } catch (e) {
            setError('Failed to create slip: ' + (e.response?.data || e.message));
        } finally {
            setSubmitting(false);
        }
    };

    const agentLabel = (a) => {
        if (!a) return '';
        const last = a.lastName ?? a.LastName ?? '';
        const first = a.firstName ?? a.FirstName ?? '';
        const alias = a.alias ?? a.Alias ?? '';
        const username = a.username ?? a.Username ?? '';
        const name = `${last} ${first}`.trim();
        return name || alias || username;
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700 }}>
                Create Extra Slip
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <Autocomplete
                        options={agents}
                        loading={loadingAgents}
                        getOptionLabel={agentLabel}
                        value={agent}
                        onChange={(_, v) => setAgent(v)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Agent *"
                                size="small"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loadingAgents ? <CircularProgress size={16} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                    <TextField
                        label="Reason *"
                        size="small"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. MAGV Payout, One-off Bonus"
                    />
                    <TextField
                        label="Description"
                        size="small"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        multiline
                        minRows={2}
                    />
                    <TextField
                        label="Amount (RM) *"
                        size="small"
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>RM</Typography> }}
                    />
                    <DatePicker
                        label="Period"
                        views={['month', 'year']}
                        value={period}
                        onChange={(v) => setPeriod(v)}
                        slotProps={{
                            textField: {
                                size: 'small',
                                fullWidth: true,
                                helperText: period && dayjs.isDayjs(period)
                                    ? `Saved as: ${formatPeriodRange(period)}`
                                    : 'Saved as "22 [prev] - 21 [month] YYYY" (matches commission slip)',
                            },
                        }}
                    />
                    {error && (
                        <Typography sx={{ color: '#B91C1C', fontSize: '0.85rem' }}>{error}</Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>Cancel</Button>
                <Button onClick={submit} variant="contained" disabled={submitting}>
                    {submitting ? 'Submitting…' : 'Submit'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
